import {
  OFFER_REMOVED, PRODUCT, RESTRICTION_ERRORS,
} from '../../constants/Errors';
import NotificationsRepository from '../../repositories/NotificationsRepository';
import Notification from '../../domain/entities/Notification/Notification';
import TranslationUtils from '../../utils/TranslationUtils';
import NotificationsCollection from '../../domain/entities/Notification/NotificationsCollection';
import { NOTIFICATION_GROUP, NOTIFICATION_TYPE, SHIPPING_NOTIFICATIONS } from '../../constants/Notifications';
import { API_NOTIFICATION_KEY_TRANSLATIONS_MAP } from '../../constants/NotificationMappedTranslations';
import { initializeNotificationManagers } from './notificationManagers';

interface NotificationsServiceConfig {
  notificationsRepository: NotificationsRepository;
  notificationsCollection: NotificationsCollection;
}

export interface INotification {
  id: string,
  key: string,
  text: string,
  products: string[],
  component?: JSX.Element;
  type: NOTIFICATION_TYPE,
}

// Provide translations for notifications by groups and notification types
export class NotificationsService {
  private notificationsRepository: NotificationsRepository;

  private notificationsCollection: NotificationsCollection;

  constructor(config: NotificationsServiceConfig) {
    this.notificationsRepository = config.notificationsRepository;
    this.notificationsCollection = config.notificationsCollection;
  }

  public initNotificationManagers(): void {
    initializeNotificationManagers();
  }

  public getNotificationsByGroup = (group: NOTIFICATION_GROUP, type?: NOTIFICATION_TYPE): INotification[] => {
    const { notificationsCollection: { notifications } } = this.notificationsRepository;

    return this.getNotifications(notifications, group, type);
  };

  public async loadTranslations(): Promise<void> {
    return this.notificationsRepository.loadNotificationsTranslations();
  }

  // Filters Messages inside collection by type and associates the corresponding translation
  private getNotifications(collection: Notification[], group: NOTIFICATION_GROUP, type?: NOTIFICATION_TYPE): INotification[] {
    const notificationsByType = collection.filter(
      (notification: Notification) => notification.group === group &&
      (type ? notification.type === type : true),
    );
    let canAddGeneric = true;
    const notifications: INotification[] = [];

    notificationsByType.forEach((notification: Notification): void => {
      const { notificationsTranslations } = this.notificationsRepository;
      // Get the translation key associated to error
      const correspondingTranslationKey = API_NOTIFICATION_KEY_TRANSLATIONS_MAP[notification.key] || 'genericError';
      const text = notificationsTranslations[correspondingTranslationKey];
      // Handling so we dont have Generic when we have translated errors
      // and so we dont have more than 1 generic message showing
      if (!canAddGeneric && correspondingTranslationKey === 'genericError') {
        notifications.push({
          id: notification.uuid,
          key: notification.key,
          products: notification.products,
          text: '',
          type: notification.type,
        });
        return;
      }

      canAddGeneric = false;

      const processedText = this.handlePlaceholders(notification, text);

      if (!notifications.find(item => item.text == processedText)) {
        notifications.push({
          id: notification.uuid,
          key: notification.key,
          products: notification.products,
          text: processedText,
          type: notification.type,
        });
      }
    });

    return notifications.filter(notification => notification.text !== '');
  }

  private handlePlaceholders = (
    notification: Notification,
    text: string,
  ): string => {
    switch (notification.key) {
      case RESTRICTION_ERRORS.SHIPPING_RESTRICTIONS_STATES:
        return TranslationUtils
          .replace(text, '::products::', notification.data.data.products as string);

      case SHIPPING_NOTIFICATIONS.PRODUCT_HAZMAT_RESTRICTION:
        return TranslationUtils
          .replace(text, '::products::', notification?.data?.data?.products as string);

      case PRODUCT.REMOVED_ITEM_SOLD_OUT:
      case PRODUCT.REMOVED_ITEM_INACTIVE:
        return TranslationUtils
          .replace(text, '::products::', this.notificationsCollection.removedProducts.join(', '));

      case OFFER_REMOVED:
        return TranslationUtils
          .replace(text, '::offer_code::', this.notificationsCollection.offerRemoved);

      default:
        return text;
    }
  };
}
